<script lang="ts">
  import { Link } from 'svelte-routing';
  import { api } from '../../lib/api/client';
  import { confirm } from '../../lib/stores/confirmDialog';
  import { acting, auth, currentUser, managedBy, type User } from '../../lib/stores/auth';
  import PageHeader from '../../lib/components/PageHeader.svelte';
  import Modal from '../../lib/components/Modal.svelte';
  import type { HealthSettings } from '../../lib/utils/health';
  import { t } from '../../lib/i18n/index.ts';

  // around splits a message at the spots a link, field, or styled word fills, in order.
  const around = (message: string) => message.split('\u0000');
  const healthAbout = around(t('settingsHealthAbout', { health: '\u0000' }));

  let error = $state('');

  let historyNote = $state('');

  async function deleteHistory() {
    if (!(await confirm(t('settingsDeleteHistoryConfirm')))) return;
    try {
      await api.delete('/me/history');
      historyNote = t('settingsHistoryDeleted');
    } catch (e: any) {
      error = e.message;
    }
  }

  let timelineNote = $state('');
  const keepTimeline = $derived($currentUser?.timeline_enabled ?? true);

  // setTimeline turns the long-term timeline on or off; off deletes it, so
  // the member confirms first (005 FR-017).
  async function setTimeline(on: boolean, box: HTMLInputElement) {
    if (!on && !(await confirm(t('settingsTimelineOffConfirm')))) {
      box.checked = true;
      return;
    }
    try {
      auth.setUser(await api.patch<User>('/me', { timeline_enabled: on }));
      timelineNote = on ? t('settingsTimelineOn') : t('settingsTimelineOff');
    } catch (e: any) {
      box.checked = !on;
      error = e.message;
    }
  }

  // Deleting some days happens in a dialog with the first and last day
  // (005 FR-017, 010 FR-081); rangeOpen is which history it deletes from.
  let rangeOpen = $state<'' | 'timeline' | 'health'>('');
  let rangeFrom = $state('');
  let rangeTo = $state('');
  let rangeError = $state('');

  function openRange(which: 'timeline' | 'health') {
    rangeFrom = '';
    rangeTo = '';
    rangeError = '';
    rangeOpen = which;
  }

  // deleteRange deletes the chosen days of the long-term timeline or of
  // health history, once confirmed in the dialog.
  async function deleteRange(e: SubmitEvent) {
    e.preventDefault();
    if (!rangeFrom || !rangeTo) return;
    try {
      if (rangeOpen === 'timeline') {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        await api.delete(`/me/timeline?from=${rangeFrom}&to=${rangeTo}&tz=${encodeURIComponent(tz)}`);
        timelineNote = t('settingsTimelineRangeDeleted', { from: rangeFrom, to: rangeTo });
      } else {
        await api.delete(`/me/health?from=${rangeFrom}&to=${rangeTo}`);
        healthNote = t('settingsHealthRangeDeleted', { from: rangeFrom, to: rangeTo });
      }
      rangeOpen = '';
    } catch (err: any) {
      rangeError = err.message;
    }
  }

  async function deleteTimeline() {
    if (!(await confirm(t('settingsDeleteTimelineConfirm')))) return;
    try {
      await api.delete('/me/timeline');
      timelineNote = t('settingsTimelineDeleted');
    } catch (e: any) {
      error = e.message;
    }
  }

  // Health history (spec 007 FR-024, 007 FR-025): how long the member keeps
  // it, 0 being as long as the server does, its export, and deleting it.
  const healthChoices: { days: number; label: string }[] = [
    { days: 0, label: t('settingsHealthSameAsServer') },
    { days: 30, label: t('settingsHealthMonths', { count: 1 }) },
    { days: 90, label: t('settingsHealthMonths', { count: 3 }) },
    { days: 365, label: t('settingsHealthYears', { count: 1 }) },
    { days: 730, label: t('settingsHealthYears', { count: 2 }) },
    { days: 1825, label: t('settingsHealthYears', { count: 5 }) },
  ];
  let health = $state<HealthSettings | null>(null);
  // healthChoice is the picked preset's days, or -1 for Custom.
  let healthChoice = $state(0);
  let healthCustom = $state('');
  let healthNote = $state('');

  api.get<HealthSettings>('/me/health/settings').then(showHealth).catch((e: any) => (error = e.message));

  function showHealth(s: HealthSettings) {
    health = s;
    const preset = healthChoices.some((c) => c.days === s.health_days);
    healthChoice = preset ? s.health_days : -1;
    healthCustom = preset ? '' : String(s.health_days);
  }

  // keptDays is how long the member's history is kept under a limit of
  // their own and the server's, 0 until deleted: the shorter applies.
  const keptDays = (own: number, server: number) => (own === 0 ? server : server === 0 ? own : Math.min(own, server));

  // setHealthDays saves the member's limit, confirming first when it keeps
  // less than now, since older days are deleted right away.
  async function setHealthDays(days: number) {
    if (!health) return;
    const before = keptDays(health.health_days, health.server_days);
    const after = keptDays(days, health.server_days);
    if (after > 0 && (before === 0 || after < before) && !(await confirm(t('settingsHealthKeepConfirm', { days: after })))) {
      showHealth(health);
      return;
    }
    try {
      showHealth(await api.put<HealthSettings>('/me/health/settings', { health_days: days }));
      healthNote = t('settingsSaved');
      error = '';
    } catch (e: any) {
      showHealth(health);
      error = e.message;
    }
  }

  function pickHealth(days: number) {
    healthNote = '';
    if (days >= 0) setHealthDays(days);
  }

  async function exportHealth(csv = false) {
    try {
      await api.download(csv ? '/me/health/export?format=csv' : '/me/health/export', csv ? 'kehai-health.zip' : 'kehai-health.json');
    } catch (e: any) {
      error = e.message;
    }
  }

  async function deleteHealth() {
    if (!(await confirm(t('settingsDeleteHealthConfirm')))) return;
    try {
      await api.delete('/me/health');
      healthNote = t('settingsHealthDeleted');
    } catch (e: any) {
      error = e.message;
    }
  }
</script>

{#if error}
  <div class="mb-4 p-3 rounded text-sm" style="background-color: var(--theme-error); color: var(--theme-base);">{error}</div>
{/if}

<!-- A child's history is their parents' to delete, so it shows read-only (spec 001 FR-098). -->
<fieldset disabled={!!$managedBy} class="min-w-0">
<div class="card p-5 mb-6">
  <PageHeader title={t('settingsLocationHistory')} level="section">
    <button class="btn btn-danger btn-sm" onclick={deleteHistory}><i aria-hidden="true" class="las la-trash mr-1"></i>{t('settingsDeleteHistory')}</button>
  </PageHeader>
  <p class="text-sm text-text-muted">{t('settingsHistoryNote')}</p>
  {#if historyNote}
    <p class="text-sm mt-2">{historyNote}</p>
  {/if}
</div>

<div class="card p-5 mb-6">
  <PageHeader title={t('settingsTimeline')} level="section">
    <button class="btn btn-danger btn-sm" onclick={deleteTimeline} disabled={!keepTimeline}><i aria-hidden="true" class="las la-trash mr-1"></i>{t('settingsDeleteTimeline')}</button>
  </PageHeader>
  <label class="flex items-center gap-2 text-sm">
    <input type="checkbox" checked={keepTimeline} onchange={(e) => setTimeline(e.currentTarget.checked, e.currentTarget)} />
    {t('settingsKeepTimeline')}
  </label>
  <p class="text-sm text-text-muted mt-2">{t('settingsTimelineNote')}</p>
  {#if keepTimeline}
    <button type="button" class="btn btn-secondary btn-sm mt-3" onclick={() => openRange('timeline')}><i aria-hidden="true" class="las la-calendar-minus mr-1"></i>{t('settingsDeleteSomeDays')}</button>
  {/if}
  {#if timelineNote}
    <p class="text-sm mt-2">{timelineNote}</p>
  {/if}
</div>

{#if health}
  {@const kept = keptDays(health.health_days, health.server_days)}
  <div class="card p-5 mb-6">
    <PageHeader title={t('settingsHealthHistory')} level="section">
      <div class="flex gap-2">
        {#if !$acting}
          <button class="btn btn-secondary btn-sm" onclick={() => exportHealth()}><i aria-hidden="true" class="las la-download mr-1"></i>{t('settingsExport')}</button>
          <button class="btn btn-secondary btn-sm" onclick={() => exportHealth(true)}><i aria-hidden="true" class="las la-file-csv mr-1"></i>{t('settingsDownloadCsv')}</button>
        {/if}
        <button class="btn btn-danger btn-sm" onclick={deleteHealth}><i aria-hidden="true" class="las la-trash mr-1"></i>{t('settingsDeleteEverything')}</button>
      </div>
    </PageHeader>
    <p class="text-sm text-text-muted mb-3">
      {healthAbout[0]}<Link to="/activities/health">{t('settingsHealthLink')}</Link>{healthAbout[1]}
    </p>
    <div class="flex flex-wrap items-end gap-3">
      <label class="text-sm">
        <span class="block mb-1">{t('settingsKeepFor')}</span>
        <select class="input" bind:value={healthChoice} onchange={() => pickHealth(healthChoice)}>
          {#each healthChoices as c (c.days)}
            <option value={c.days}>{c.label}</option>
          {/each}
          <option value={-1}>{t('settingsCustom')}</option>
        </select>
      </label>
      {#if healthChoice === -1}
        <form class="flex items-end gap-2" onsubmit={(e) => { e.preventDefault(); if (Number(healthCustom) >= 1) setHealthDays(Math.round(Number(healthCustom))); }}>
          <label class="text-sm">
            <span class="block mb-1">{t('settingsDays')}</span>
            <input class="input w-28" type="number" min="1" max="36500" bind:value={healthCustom} required oninput={() => (healthNote = '')} />
          </label>
          <button type="submit" class="btn btn-secondary btn-sm">{t('appSave')}</button>
        </form>
      {/if}
    </div>
    <p class="text-xs text-text-muted mt-2">
      {#if health.server_days > 0 && (health.health_days === 0 || health.server_days < health.health_days)}
        {health.health_days > 0 ? t('settingsHealthServerShorter', { days: health.server_days }) : t('settingsHealthServerKeeps', { days: health.server_days })}
      {:else if kept === 0}
        {t('settingsHealthKeptForever')}
      {:else}
        {t('settingsHealthOlderDeleted', { days: kept })}
      {/if}
    </p>
    <button type="button" class="btn btn-secondary btn-sm mt-3" onclick={() => openRange('health')}><i aria-hidden="true" class="las la-calendar-minus mr-1"></i>{t('settingsDeleteSomeDays')}</button>
    {#if healthNote}
      <p class="text-sm mt-2">{healthNote}</p>
    {/if}
  </div>
{/if}
</fieldset>

{#if rangeOpen}
  {@const timeline = rangeOpen === 'timeline'}
  <Modal title={timeline ? t('settingsTimelineRangeTitle') : t('settingsHealthRangeTitle')} onclose={() => (rangeOpen = '')}>
    <form class="flex flex-col gap-3" onsubmit={deleteRange}>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label class="text-sm">
          <span class="block mb-1">{t('settingsFirstDay')}</span>
          <input type="date" class="input w-full" bind:value={rangeFrom} max={rangeTo || undefined} required oninput={() => (rangeError = '')} />
        </label>
        <label class="text-sm">
          <span class="block mb-1">{t('settingsLastDay')}</span>
          <input type="date" class="input w-full" bind:value={rangeTo} min={rangeFrom || undefined} required oninput={() => (rangeError = '')} />
        </label>
      </div>
      {#if rangeFrom && rangeTo}
        <p class="text-sm">{timeline ? t('settingsTimelineRangeConfirm', { from: rangeFrom, to: rangeTo }) : t('settingsHealthRangeConfirm', { from: rangeFrom, to: rangeTo })}</p>
      {/if}
      {#if rangeError}<p class="text-sm text-error" role="alert">{rangeError}</p>{/if}
      <div class="flex justify-end gap-2">
        <button type="button" class="btn btn-secondary" onclick={() => (rangeOpen = '')}>{t('appCancel')}</button>
        <button type="submit" class="btn btn-danger" disabled={!rangeFrom || !rangeTo}><i aria-hidden="true" class="las la-trash mr-1"></i>{t('settingsDeleteTheseDays')}</button>
      </div>
    </form>
  </Modal>
{/if}
